import { useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@/components/ui/popover';
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '@/components/ui/command';
import { cn } from '@/lib/utils';

interface SlackChannel {
  ChannelId: string;
  ChannelName: string;
  Subscriptions: string[];
  IsBotMember: boolean;
}

interface SlackChannelConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  channelId: string;
  channelName: string;
  currentSubscriptions: string[];
  isBotMember: boolean;
  availableChannels?: SlackChannel[];
  onSave: (channelId: string, subscriptions: string[]) => void;
  onAddChannel?: () => void;
}

const notificationTypes = [
  {
    id: 'subscribe',
    label: 'PR summaries',
    command: '/subscribe',
    description: 'Summaries of pull request activity',
  },
  {
    id: 'sprint',
    label: 'Sprint reports',
    command: '/sprint',
    description: 'Sprint progress and insights',
  },
  // {
  //   id: 'insights',
  //   label: 'Team insights',
  //   command: '/insights',
  //   description: 'Team performance insights and analytics',
  // },
  {
    id: 'leaderboard',
    label: 'Leaderboard updates',
    command: '/leaderboard',
    description: 'Leaderboard rankings and updates',
  },
  {
    id: 'security',
    label: 'Security PR notifications',
    command: '/security',
    description:
      'Notifications when auto-fix PRs are created for security issues',
  },
  {
    id: 'eku',
    label: 'Daily incident digest',
    command: '/eku',
    description:
      'Daily summary of new error patterns noticed and ones that got verified-fixed',
  },
];

const DEFAULT_AVAILABLE_CHANNELS: SlackChannel[] = [];

export default function SlackChannelConfigModal({
  isOpen,
  onClose,
  channelId,
  channelName,
  currentSubscriptions,
  isBotMember,
  availableChannels = DEFAULT_AVAILABLE_CHANNELS,
  onSave,
  onAddChannel,
}: SlackChannelConfigModalProps) {
  const [channelState, setChannelState] = useState(() => ({
    selectedChannelId: channelId,
    selectedChannelName: channelName,
    selectedSubscriptions: currentSubscriptions,
    currentIsBotMember: isBotMember,
  }));
  const {
    selectedChannelId,
    selectedChannelName,
    selectedSubscriptions,
    currentIsBotMember,
  } = channelState;
  const [pickerOpen, setPickerOpen] = useState(false);

  const handleChannelChange = (newChannelId: string) => {
    const channel = availableChannels.find(
      (ch) => ch.ChannelId === newChannelId
    );
    if (channel) {
      setChannelState({
        selectedChannelId: channel.ChannelId,
        selectedChannelName: channel.ChannelName,
        selectedSubscriptions: channel.Subscriptions,
        currentIsBotMember: channel.IsBotMember,
      });
      setPickerOpen(false);
    }
  };

  const handleAddNewChannel = () => {
    // Close the current config modal and trigger the add channel modal
    setPickerOpen(false);
    onClose();
    if (onAddChannel) {
      onAddChannel();
    }
  };

  const handleToggle = (id: string) => {
    setChannelState({
      ...channelState,
      selectedSubscriptions: channelState.selectedSubscriptions.includes(id)
        ? channelState.selectedSubscriptions.filter((s) => s !== id)
        : [...channelState.selectedSubscriptions, id],
    });
  };

  const handleSave = () => {
    onSave(selectedChannelId, selectedSubscriptions);
    onClose();
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle
            className="truncate"
            title={`Configure #${selectedChannelName}`}
          >
            Configure #{selectedChannelName}
          </DialogTitle>
        </DialogHeader>

        <div>
          {/* Slack Channel */}
          <div className="mb-4">
            <label
              htmlFor="slack-channel-config-channel-dropdown"
              className="block text-xs font-medium mb-1.5 text-ds-text-default"
            >
              Slack Channel
            </label>
            {availableChannels.length > 0 ? (
              <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
                <PopoverTrigger
                  render={
                    <Button
                      id="slack-channel-config-channel-dropdown"
                      variant="outline"
                      className="w-full justify-between"
                    >
                      <span>#{selectedChannelName}</span>
                      <ChevronDown
                        className={cn(
                          'transition-transform',
                          pickerOpen && 'rotate-180'
                        )}
                      />
                    </Button>
                  }
                />
                <PopoverContent
                  align="start"
                  className="w-[var(--anchor-width)] p-0"
                >
                  <Command>
                    <CommandInput placeholder="Search channels..." />
                    <CommandList>
                      <CommandEmpty>No channels found</CommandEmpty>
                      <CommandGroup>
                        {availableChannels.map((channel) => (
                          <CommandItem
                            key={channel.ChannelId}
                            value={channel.ChannelName}
                            onSelect={() =>
                              handleChannelChange(channel.ChannelId)
                            }
                          >
                            <span className="flex-1">
                              #{channel.ChannelName}
                            </span>
                            {channel.ChannelId === selectedChannelId && (
                              <Check className="text-ds-brand-default" />
                            )}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                  <div className="border-t border-ds-border-subtlest">
                    <Button
                      variant="ghost"
                      className="w-full justify-center text-ds-text-subtle"
                      onClick={handleAddNewChannel}
                    >
                      Channel not here?
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>
            ) : (
              <div className="w-full px-3 py-2 rounded-lg border border-ds-border-subtler text-sm bg-ds-gray-subtlest text-ds-text-darker">
                #{selectedChannelName}
              </div>
            )}
          </div>

          {/* Bot Status Message */}
          {!currentIsBotMember && (
            <div className="mb-4 p-3 rounded-lg border border-ds-brand-subtler bg-ds-brand-lightest text-ds-brand-default">
              <div className="flex items-start gap-2">
                <svg
                  className="w-4 h-4 mt-0.5 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>
                <div className="text-sm">
                  <p className="font-medium">Bot will be added automatically</p>
                  <p className="text-xs mt-1 opacity-90">
                    When you enable any notification, the Entelligence bot will
                    be automatically added to this channel.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Notification Types */}
          <div>
            <span className="block text-xs font-medium mb-2 text-ds-text-default">
              Notification Types
            </span>
            <div className="flex flex-col gap-1.5 max-h-80 overflow-y-auto pr-1">
              {notificationTypes.map((type) => (
                <label
                  key={type.id}
                  htmlFor={`subscription-${type.id}`}
                  className="flex items-start gap-2.5 p-3 rounded-lg border border-ds-border-subtlest hover:bg-ds-gray-subtlest cursor-pointer transition-colors"
                >
                  <Checkbox
                    id={`subscription-${type.id}`}
                    checked={selectedSubscriptions.includes(type.id)}
                    onCheckedChange={() => handleToggle(type.id)}
                    className="mt-0.5 shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <span className="text-sm font-medium text-ds-text-darker">
                        {type.label}
                      </span>
                      <code className="text-3xs px-1.5 py-0.5 rounded bg-ds-gray-subtlest text-ds-text-subtle border border-ds-border-subtlest">
                        {type.command}
                      </code>
                    </div>
                    <p className="text-xs text-ds-text-subtle">
                      {type.description}
                    </p>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave}>Save Changes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
